import React from 'react';
import { Extra } from '../../models/types';
import {
    formatExtraPriceDisplay,
    formatExtraUnitsLabel,
} from '../../utils/extraPricing';
import { resolveExtraIconKind } from '../../utils/extraIcons';
import { CheckIcon, ExtraIcon, PlusIcon } from './ExtraIcons';
import './ExtraItem.css';

interface ExtraItemProps {
    name: string;
    priceLabel?: string;
    unitsLabel?: string;
    selected?: boolean;
    variant?: 'optional' | 'included';
    onAdd?: () => void;
    onRemove?: () => void;
    children?: React.ReactNode;
    /** When true, show + even if selected (quantity extras). */
    canAdjustAmount?: boolean;
}

const currencyFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
});

/** Build display props from an optional Extra catalog item. */
export function getOptionalExtraCardLabels(
    extra: Extra,
    selected: boolean
): { priceLabel: string; unitsLabel: string; canAdjustAmount: boolean } {
    const unitsLabel = selected ? formatExtraUnitsLabel(extra) : '';
    const priceLabel = selected
        ? formatExtraPriceDisplay(extra, currencyFormatter)
        : extra.info_type === 'ask_shop'
            ? 'Consultar con centro'
            : extra.extra_cost_str;
    const canAdjustAmount = extra.info_type === 'dialog_int_amount';

    return { priceLabel, unitsLabel, canAdjustAmount };
}

const ExtraItem: React.FC<ExtraItemProps> = ({
    name,
    priceLabel,
    unitsLabel,
    selected = false,
    variant = 'optional',
    onAdd,
    onRemove,
    children,
    canAdjustAmount = false,
}) => {
    const isIncluded = variant === 'included';
    const showAdd = !isIncluded && onAdd && (!selected || canAdjustAmount);
    const showRemove = !isIncluded && selected && onRemove;
    const showIncludedBadge = isIncluded;
    const isActionable = Boolean(showAdd);
    const showUnits = Boolean(unitsLabel);

    const className = [
        'extra-card',
        isIncluded ? 'extra-card--included' : 'extra-card--optional',
        selected ? 'extra-card--selected' : '',
        children ? 'extra-card--with-options' : '',
        isActionable ? 'extra-card--actionable' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const handleCardActivate = () => {
        if (!showAdd || !onAdd) {
            return;
        }
        onAdd();
    };

    return (
        <div
            className={className}
            onClick={isActionable ? handleCardActivate : undefined}
        >
            <div className="extra-card-top">
                <span className="extra-card-icon" aria-hidden="true">
                    <ExtraIcon kind={resolveExtraIconKind(name)} />
                </span>
                <div className="extra-card-name">{name}</div>
            </div>

            <div className="extra-card-actions">
                {showIncludedBadge && (
                    <span className="extra-card-check extra-card-check--static" aria-hidden="true">
                        <CheckIcon />
                    </span>
                )}

                {showRemove && (
                    <button
                        type="button"
                        className="extra-card-check"
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemove();
                        }}
                        aria-label={`Quitar ${name}`}
                    >
                        <CheckIcon />
                    </button>
                )}

                {showAdd && (
                    <button
                        type="button"
                        className="extra-card-add"
                        onClick={(event) => {
                            event.stopPropagation();
                            onAdd();
                        }}
                        aria-label={selected ? `Ajustar unidades de ${name}` : `Añadir ${name}`}
                    >
                        <PlusIcon />
                    </button>
                )}
            </div>

            <div className="extra-card-body">
                {showUnits && (
                    <div className="extra-card-units">{unitsLabel}</div>
                )}
                {children && (
                    <div className="extra-card-options" onClick={(event) => event.stopPropagation()}>
                        {children}
                    </div>
                )}
                {priceLabel && (
                    <div className="extra-card-price">{priceLabel}</div>
                )}
            </div>
        </div>
    );
};

export default ExtraItem;
